/**
 * Reading a page, once, for both callers who need it.
 *
 * Two paths ask the same question of the same endpoints and must get the same answer:
 *
 *  - `useDeckCreate.createWebsite` — the intake's submit. Fetches FIRST and mints the card already
 *    resolved, because the row must not appear in the directory until there is a website to show
 *    (Beka, 2026-09-05: the field is a starting point, not a card you have already made).
 *  - `useWebActions.resolve` — a card that already exists and has no address yet, which is what a
 *    blank web card made on the canvas (`addEmptyWeb`) still is when you open it here.
 *
 * They differ only in what they do with the result — mint or patch — so the reading itself lives
 * here. It was inlined in both for one afternoon and the two immediately disagreed about which
 * status a scrape with no screenshot deserves.
 *
 * NO WRITES. This module never touches the blob; it takes an address and returns what the page said.
 */
import {
  isSocialPostUrl,
  SOCIAL_CARD_SIZE,
  WEB_CARD_DEFAULT_SIZE,
  type BirtvyWeb,
} from '~/design-system/components/DeckGen/birtvy/web/birtvyWeb';
import { toWebHref } from '~/design-system/components/DeckGen/utils/imageElement';

/** The shape both endpoints answer in. Every field optional — a scrape is allowed to come back thin. */
interface FetchedPreview {
  url?: string;
  title?: string;
  screenshotUrl?: string;
  images?: string[];
  frameable?: boolean;
  platform?: string;
  author?: string;
  authorAvatar?: string;
  text?: string;
  videos?: { url: string; poster?: string; width?: number; height?: number }[];
}

export interface WebPreview {
  /** The canonical address to store — the redirect target when there was one, else what was typed. */
  url: string;
  /** The page answered with something worth drawing. `false` puts the card in its `error` state. */
  ready: boolean;
  /** The card's fields, ready to spread over a new card or an existing one. */
  patch: Partial<BirtvyWeb>;
  /** Site default, or the portrait post shape once we know it is one. */
  size: { width: number; height: number };
}

/**
 * Fetch a page and derive the card's fields.
 *
 * Never throws and never rejects: a network failure returns `ready: false` with the address the user
 * gave, because the card they asked for should exist and say it could not be reached — losing the
 * typed address to an exception is the one outcome that wastes their input.
 *
 * The social fork is the canvas's (`useBirtvyWebIngest`): x.com and instagram.com block both iframing
 * and screenshotting, so those go to the extractor and the card renders the POST. Without the fork an
 * x.com link comes back with no screenshot and lands as `offline`, which is not what happened.
 */
export async function fetchWebPreview(conversationId: string, entered: string): Promise<WebPreview> {
  const url = toWebHref(entered);
  const social = isSocialPostUrl(url);
  const cid = encodeURIComponent(conversationId);
  const endpoint = social
    ? `/conversation-social-extract-api/${cid}`
    : `/conversation-web-preview-api/${cid}`;

  let fetched: FetchedPreview | null = null;
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    if (res.ok) fetched = await res.json();
  } catch {
    /* left null — the caller lands the card in `error` with the address the user gave */
  }

  const post =
    social && fetched && (fetched.text || (fetched.images?.length ?? 0) > 0)
      ? {
          platform: fetched.platform ?? 'social',
          author: fetched.author,
          authorAvatar: fetched.authorAvatar,
          text: fetched.text ?? '',
          images: fetched.images ?? [],
          videos: fetched.videos ?? [],
        }
      : undefined;
  const ready = social ? !!post : !!fetched?.screenshotUrl;
  const size = post ? SOCIAL_CARD_SIZE : WEB_CARD_DEFAULT_SIZE;

  return {
    url: fetched?.url ?? url,
    ready,
    size,
    patch: {
      title: fetched?.title ?? '',
      screenshotUrl: fetched?.screenshotUrl,
      frameable: fetched?.frameable ?? false,
      sourceImages: fetched?.images ?? [],
      post,
      status: ready ? 'ready' : 'error',
      // A redirect is where the page actually IS; the typed address is only how we got there.
      url: fetched?.url ?? url,
      ...(ready ? { checkedAt: Date.now() } : {}),
    },
  };
}
